import classnames from 'classnames';
import React from 'react';

import { ErrorMessage } from '@eika/attention-message';

import { useValidateBank, useValidateIban } from '../../../../../fetchHooks/useCrossBorderBankValidation';
import useBankCountry from '../../../../../hooks/useBankCountry';
import useReceiverResidenceCountry from '../../../../../hooks/useReceiverResidenceCountry';
import useTranslation from '../../../../../hooks/useTranslation';
import useFormContext from '../../form/useFormContext';

const ReceiverSummary: React.FC = () => {
    const translated = useTranslation();
    const { state } = useFormContext();

    const { data: ibanValidation, error: ibanValidationError } = useValidateIban(
        state.receiver?.accountNumber ? { value: state.receiver.accountNumber } : undefined,
        state.receiver?.accountType === 'IBAN',
    );

    const { data: bankValidation, error: bankValidationError } = useValidateBank(
        state.receiver?.bic,
        state.receiver?.nationalId,
        state.receiver?.bankCountryCode,
        state.receiver?.accountType === 'BBAN',
    );

    const bankInformation = ibanValidation || bankValidation;

    const receiverBankCountry = useReceiverResidenceCountry(state.receiver?.receiverCountryCode);

    const bankCountry = useBankCountry(state.receiver?.bankCountryCode || state.country?.countryCode);
    const bic = state.bankValidation?.bank?.bic;

    return (
        <li key="receiverSummary" className="receiver__list-item">
            <h3 className="summary-description__title">{translated.crossBorderPayment.summary.receiver}</h3>
            <dl className="summary-description__term-list">
                <dt className="summary-description__term summary-description__spacing-to-title">{translated.name}</dt>
                <dd className="summary-description__info summary-description__extra-spacing">
                    {state.receiver?.name || state.receiverName}
                </dd>
                <dt className="summary-description__term">
                    {state.receiver?.accountType ? translated.crossBorderPayment.label[state.receiver.accountType] : ''}
                </dt>
                <dd className="summary-description__info summary-description__extra-spacing">
                    {state.receiver?.accountNumber}
                </dd>
                {bic?.value && (
                    <>
                        <dt className="summary-description__term">
                            {translated.crossBorderPayment.newReceiver.bicLabel}
                        </dt>
                        <dd className="summary-description__info summary-description__extra-spacing">{bic?.value}</dd>
                    </>
                )}

                <dt className="summary-description__term">
                    {translated.crossBorderPayment.newReceiver.nationalIdLabel}
                </dt>
                <dd className="summary-description__info summary-description__extra-spacing">
                    {state.receiver?.nationalId?.value || state.bankValidation?.bank?.nationalId?.value}
                </dd>
                <dt className="summary-description__term">{translated.crossBorderPayment.newReceiver.receiverBank}</dt>
                {ibanValidationError || bankValidationError ? (
                    <ErrorMessage text={translated.crossBorderPayment.newReceiver.bankDetailsError} />
                ) : (
                    <>
                        <dd className="summary-description__info">{bankInformation?.bank?.address}</dd>
                        <dd
                            className={classnames('summary-description__info', {
                                'summary-description__extra-spacing': state.receiver?.address,
                            })}
                        >
                            {bankCountry?.name.toUpperCase()}
                        </dd>
                    </>
                )}
                {state.receiver?.address && (
                    <>
                        <dt className="summary-description__term">
                            {translated.crossBorderPayment.newReceiver.receiverAddress}
                        </dt>
                        <dd className="summary-description__info">{state.receiver?.address}</dd>
                        <dd className="summary-description__info">{state.receiver?.address2 ?? ''}</dd>
                        <dd className="summary-description__info">{`${state.receiver?.postcode ?? ''} ${
                            state.receiver?.town ?? ''
                        }`}</dd>
                        <dd className="summary-description__info">{receiverBankCountry?.name}</dd>
                    </>
                )}
            </dl>
        </li>
    );
};

export default ReceiverSummary;
